import axios, { AxiosError } from 'axios';

// In production, use relative URL (nginx proxies /api to backend)
// In development, can override with the VITE_API_URL env var
const API_URL = import.meta.env.VITE_API_URL || '/api';

// Helper to check if token is valid (not null, undefined, or literal strings)
const isValidToken = (token: string | null): token is string => {
  return token !== null &&
    token !== undefined &&
    token !== 'null' &&
    token !== 'undefined' &&
    token.trim() !== '';
};

// Get valid token from storage
export const getAuthToken = (): string | null => {
  const token = localStorage.getItem('dsshield_token') || localStorage.getItem('token');
  return isValidToken(token) ? token : null;
};

// Clear auth tokens and redirect to login
export const clearAuthAndRedirect = () => {
  localStorage.removeItem('dsshield_token');
  localStorage.removeItem('token');
  // Only redirect if not already on login page
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }
};

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('dsshield_token') || localStorage.getItem('token');
  if (token && token !== 'null' && token !== 'undefined') {
    // Axios v1 uses AxiosHeaders internally; keep typing flexible here.
    if (config.headers) {
      (config.headers as any).Authorization = `Bearer ${token}`;
    } else {
      config.headers = { Authorization: `Bearer ${token}` } as any;
    }
  }
  return config;
});

// Response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Try to refresh token
      const refreshToken = localStorage.getItem('dsshield_refresh_token');
      if (refreshToken && refreshToken !== 'null') {
        try {
          const refreshResponse = await axios.post(`${API_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          if (refreshResponse.data.token) {
            localStorage.setItem('dsshield_token', refreshResponse.data.token);
            localStorage.setItem('dsshield_refresh_token', refreshResponse.data.refresh_token);

            // Retry the original request
            error.config.headers.Authorization = `Bearer ${refreshResponse.data.token}`;
            return api.request(error.config);
          }
        } catch (refreshError) {
          // Refresh failed, clear auth and redirect to login
          localStorage.removeItem('dsshield_token');
          localStorage.removeItem('dsshield_refresh_token');
          window.location.href = '/login';
        }
      } else {
        // No refresh token, redirect to login
        localStorage.removeItem('dsshield_token');
        localStorage.removeItem('dsshield_refresh_token');
        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    }
    // The server refuses console requests while the on-prem licence is not
    // valid. AuthContext reloads the licence and the app shell sends the
    // user to Settings > License, or to a page that explains the block.
    if (error.response?.status === 403 && error.response?.data?.code === 'LICENSE_REQUIRED') {
      window.dispatchEvent(new CustomEvent('dsshield:license-required'));
    }
    return Promise.reject(error);
  }
);

// Policy API
export const policiesApi = {
  getAll: () => api.get('/policies'),
  get: (id: number) => api.get(`/policies/${id}`),
  create: (data: any) => api.post('/policies', data),
  update: (id: number, data: any) => api.put(`/policies/${id}`, data),
  delete: (id: number) => api.delete(`/policies/${id}`),
  addRule: (policyId: number, rule: any) => api.post(`/policies/${policyId}/rules`, rule),
  deleteRule: (ruleId: number) => api.delete(`/rules/${ruleId}`),
};

// Analytics API - Enterprise Dashboard
export const analyticsApi = {
  getDashboard: (period?: string) => api.get('/analytics/dashboard', { params: { period } }),
  getTopDomains: (period?: string, limit?: number) => api.get('/analytics/top-domains', { params: { period, limit } }),
  getTopUsers: (period?: string, limit?: number) => api.get('/analytics/top-users', { params: { period, limit } }),
  getHeatmap: (period?: string) => api.get('/analytics/heatmap', { params: { period } }),
};

// Alerts API - User Behavior Alerts
export const alertsApi = {
  getAll: (status?: string, severity?: string) => api.get('/alerts', { params: { status, severity } }),
  acknowledge: (id: number, acknowledgedBy?: string) => api.post(`/alerts/${id}/acknowledge`, { acknowledged_by: acknowledgedBy }),
  resolve: (id: number) => api.post(`/alerts/${id}/resolve`),
};

// Notifications API
export const notificationsApi = {
  getAll: (unreadOnly?: boolean) => api.get('/notifications', { params: { unread: unreadOnly } }),
  markRead: (id: number) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

// Device Identity & Management API
export const devicesApi = {
  getAll: (params?: { status?: string; platform?: string; search?: string; limit?: number; offset?: number }) =>
    api.get('/devices', { params }),
  get: (id: string) => api.get(`/devices/${id}`),
  getTimeline: (id: string, params?: { limit?: number; type?: string }) =>
    api.get(`/devices/${id}/timeline`, { params }),
  getLifecycle: (id: string, params?: { limit?: number; type?: string }) =>
    api.get(`/devices/${id}/lifecycle`, { params }),
  getAttempts: (id: string, limit?: number) =>
    api.get(`/devices/${id}/attempts`, { params: { limit } }),
  getComplianceStatus: (id: string) => api.get(`/devices/${id}/compliance`),
  getComplianceReports: (id: string) => api.get(`/devices/${id}/compliance/reports`),
};

// Compliance Rules API
export const complianceRulesApi = {
  getAll: (params?: { platform?: string; category?: string; enabled?: boolean }) =>
    api.get('/compliance-rules', { params }),
  get: (id: string) => api.get(`/compliance-rules/${id}`),
  create: (data: any) => api.post('/compliance-rules', data),
  update: (id: string, data: any) => api.put(`/compliance-rules/${id}`, data),
  delete: (id: string) => api.delete(`/compliance-rules/${id}`),
  toggle: (id: string) => api.post(`/compliance-rules/${id}/toggle`),
  seed: () => api.post('/compliance-rules/seed'),
  getStats: () => api.get('/compliance-rules/stats'),
  getCategories: () => api.get('/compliance-rules/categories'),
};

// Blocked Events API
export const blockedEventsApi = {
  getAll: (params?: { period?: string; category?: string; event_type?: string; action?: string; device_id?: string; domain?: string; limit?: number; offset?: number }) =>
    api.get('/blocked-events', { params }),
  getStats: (period?: string) => api.get('/blocked-events/stats', { params: { period } }),
};
